import React from 'react';
import { Award, Heart, Lightbulb, Users, Quote } from 'lucide-react';
import { aboutContent, testimonials } from '../data/mock';

const iconMap = {
  'Excelência': Award,
  'Confiança': Heart,
  'Inovação': Lightbulb,
  'Personalização': Users,
};

const About = () => {
  return (
    <section id="sobre" className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#C1A54F]/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#E5B1C4]/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            {aboutContent.title}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-gold-gradient mb-6">
            {aboutContent.subtitle}
          </h2>
          <div className="flex items-center justify-center">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#C1A54F]" />
            <span className="mx-4 text-[#E1C57C]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#C1A54F]" />
          </div>
        </div>

        {/* About Content with Photo */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
          {/* Sandra's Photo */}
          <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
            <div className="relative">
              {/* Decorative frame */}
              <div className="absolute -inset-4 border border-[#C1A54F]/20 rounded-sm" />
              <div className="absolute -inset-8 border border-[#E5B1C4]/10 rounded-sm" />
              
              {/* Photo container */}
              <div className="relative overflow-hidden rounded-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_beautywellness-2/artifacts/atr1biia_sandrasilva.webp"
                  alt="Sandra Silva - Fundadora da Shape & Shine"
                  className="w-full max-w-md h-auto object-cover"
                  style={{ aspectRatio: '1/1', objectPosition: 'center top' }}
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 via-transparent to-transparent" />
              </div>
              
              {/* Name tag */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#C1A54F] to-[#E1C57C] px-6 py-2">
                <span className="text-[#0a0a0a] font-medium tracking-wide text-sm">Sandra Silva</span>
              </div>
              
              {/* Decorative corner elements */}
              <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-[#E5B1C4]/40" />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-[#E5B1C4]/40" />
            </div>
          </div>

          {/* Text Content */}
          <div className="order-1 lg:order-2">
            <h3 className="font-display text-3xl text-[#E5B1C4] mb-6">
              A Paixão pela Beleza
            </h3>
            {aboutContent.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#bbb5ae] text-lg leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
            
            {/* Signature */}
            <div className="mt-8 pt-6 border-t border-[#C1A54F]/20">
              <p className="font-display text-2xl text-gold-gradient italic">
                Sandra Silva
              </p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-24">
          <h3 className="font-display text-2xl text-center text-[#E1C57C] mb-10">
            Os Nossos Valores
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutContent.values.map((value, index) => {
              const IconComponent = iconMap[value.title] || Award;
              return (
                <div
                  key={index}
                  className="p-6 bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#C1A54F]/10
                           rounded-sm hover:border-[#C1A54F]/30 transition-all duration-300 group text-center"
                >
                  <div className="w-12 h-12 mx-auto rounded-full border border-[#C1A54F]/30 flex items-center justify-center
                                mb-4 group-hover:border-[#C1A54F] group-hover:bg-[#C1A54F]/10 transition-all">
                    <IconComponent className="text-[#E1C57C]" size={20} />
                  </div>
                  <h4 className="font-display text-xl text-[#f5f0e8] mb-2">
                    {value.title}
                  </h4>
                  <p className="text-[#bbb5ae] text-sm">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <h3 className="font-display text-3xl text-center text-[#E5B1C4] mb-12">
            O Que Dizem as Nossas Clientes
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-8 bg-[#111111] border border-[#E5B1C4]/10 rounded-sm relative
                         hover:border-[#E5B1C4]/30 transition-all duration-300"
              >
                <Quote className="absolute top-6 right-6 text-[#E5B1C4]/20" size={32} />
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[#E1C57C] mr-1">★</span>
                  ))}
                </div>
                <p className="text-[#bbb5ae] italic mb-6 relative z-10">
                  "{testimonial.text}"
                </p>
                <p className="text-[#E5B1C4] font-medium">
                  {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
